/**
 * Everything the theater shows for one run: loads the manifest's assets into the Viewer,
 * applies presets, drives frames / cine, the commissural views, the root crop, the MPR set and
 * the first-open thumbnail. React reads `state` and subscribes to `onChange`.
 */
import * as THREE from 'three';
import { api, type AssetEntry, type Manifest } from '@/api/client';
import { ByteBudgetCache, fetchBytes, loadVolume, makeData3DTexture, type VolumeData } from '@/assets/loader';
import { DEFAULT_COLORS, type Rgba } from '@/inventory/colors';
import { DEFAULT_OFF, readInventoryVisibility, SECTIONS, SEGMENTATION_KEYS } from '@/inventory/model';
import { MprSet, type PaneId } from '@/mpr/MprSet';
import { findPoint, parseMeasurements, type MeasurementRow } from '@/prediction/measurements';
import { PredictionObject } from '@/prediction/PredictionObject';
import { SurfaceSet, type SurfaceItem } from '@/surfaces/SurfaceSet';
import { DEFAULT_PRESET, LOAD_DEFAULTS, PRESETS, type PresetDefinition, type RenderMode } from '@/volume/presets';
import { histogramCounts, normalizeLog, shiftHuToNormalized, tfEdit } from '@/volume/histogram';
import { huToNormalized, NEUTRAL_DOMAIN, normalizedToHu, type TfDomain, type TransferFunctionJson } from '@/volume/tf';
import { VolumeObject, type CuspFieldBinding } from '@/volume/VolumeObject';
import { cuspCapPlane, LEAFLET_DEFAULT_EMPHASIS, LEAFLET_DEFAULT_FILL, LEAFLET_DEFAULT_OPACITY, leafletFieldAssets, parseLeafletFieldRecord, type LeafletFieldRecord } from '@/volume/leaflets';
import { applyCleanRootPalette, cleanRootAssets, cleanRootClipPlanes, cutawayCleanWindow } from '@/volume/cleanRoot';
import { TF_CARDIAC_VR_CUTAWAY_CLEAN } from '@/volume/presets';
import { axisLabelsFromOrientation, DEFAULT_AXIS_LABELS, MM_PER_UNIT, sceneToUvw, volumeExtentMm, type AxisLabels, type VolumeGrid } from './frame';
import { lookRotation } from '@/mpr/MprSet';
import { LAYER_MAIN, LAYER_OVERLAY, Viewer } from './Viewer';
import { Corrections, type CorrectionsSummary, type ContourDef, type SnapMode } from '@/corrections/Corrections';
import { EditController } from '@/corrections/EditController';
import { HandleSet } from '@/corrections/HandleSet';

export type CommissureId = 'RN' | 'RL' | 'LN';

/** The leaflet overlay's state for the active frame: off (the preset has none), the field is
 * downloading, bound, the run ships none for this frame, or the download failed. */
export type LeafletStatus = 'off' | 'loading' | 'ready' | 'missing' | 'failed';

/** A frame's leaflet field on the GPU (VolumetricObject.LeafletFields PipelineLeafletField). */
interface CuspFrame {
  binding: CuspFieldBinding;
  record: LeafletFieldRecord;
  byteLength: number;
}

/** The clean-root set's state for the active frame: off (the preset uses none), downloading,
 * bound, the run ships no clean volume for this frame, or the download failed. */
export type CleanRootStatus = 'off' | 'loading' | 'ready' | 'missing' | 'failed';

/** A frame's clean bundle on the GPU (VolumetricObject.CutawayClean CutawayCleanBundle): the
 * clean volume, the root region mask (null when the run shipped none) and the clean TF (the
 * run's per-scan file repainted with the inside-root palette, else the bundled clean TF). */
interface CleanFrame {
  texture: THREE.Data3DTexture;
  region: THREE.Data3DTexture | null;
  tf: TransferFunctionJson;
  /** The run shipped its own .tf (the window then stays full, as on the desktop). */
  hasOwnTf: boolean;
  /** The record's measured window, for the resource-TF path. */
  window: [number, number] | null;
  byteLength: number;
}

export interface InventoryRowState {
  key: string;
  present: boolean;
  visible: boolean;
  color: Rgba;
  value: string;
  info: [string, string][];
}

export interface TheaterState {
  loading: string | null;
  error: string | null;
  /** Segmentation surfaces that failed to load (names); shown in the inventory. */
  surfaceErrors: string[];
  presetName: string;
  availablePresets: string[];
  window: [number, number];
  boxOpacity: number;
  cameraClip: number;
  lighting: boolean;
  renderMode: RenderMode;
  samplingMultiplier: number;
  cubic: boolean;
  /** Render coarser while the camera moves (Viewer.reduceWhileInteracting). */
  reduceWhileMoving: boolean;
  /** The authored transfer function (base axis), the baked domain and the shader-section state. */
  tf: TransferFunctionJson;
  tfDomain: TfDomain;
  tfShiftHu: number;
  tfStretch: number;
  tfEdited: boolean;
  gradientThreshold: number;
  /** The active preset renders the run's leaflet field on top of the volume. */
  leafletOverlay: boolean;
  leafletStatus: LeafletStatus;
  /** Leaflet fill (0 none, 1 evidence surface, 2 complete geometric leaflet) and the leaflet set's opacity. */
  leafletFill: number;
  leafletOpacity: number;
  /** The clean volume's state for the active frame (Clean Root: the split second volume; Emptied: the bound volume). */
  cleanRootStatus: CleanRootStatus;
  /** The Clean Root tile's raw-set / clean-set transparency sliders, as opacity (0..1). */
  rawSetOpacity: number;
  cleanSetOpacity: number;
  /** Show the leaflet fill (red) in the MPR panes. */
  mprLeaflets: boolean;
  histogram: Float32Array | null;
  histogramRegionOnly: boolean;
  histogramCaption: string;
  orthographic: boolean;
  showVolume: boolean;
  showLabels: boolean;
  hideAll: boolean;
  frame: number;
  /** Frame whose CT volume is being fetched after a frame change (null when idle). */
  pendingFrame: number | null;
  /** Message of the last failed frame fetch (cleared on the next successful change). */
  frameError: string | null;
  /** Frames whose CT volume is resident in GPU memory. */
  loadedFrames: ReadonlySet<number>;
  playing: boolean;
  fps: number;
  commissureView: CommissureId | null;
  mprWindow: [number, number];
  mprGain: number;
  mprContrast: number;
  mprZoom: number;
  mprOffsets: Record<PaneId, number>;
  mprOverlays: boolean;
  mprLabels: boolean;
  mprPlanesIn3D: Record<PaneId, boolean>;
  rows: Map<string, InventoryRowState>;
  axisLabels: AxisLabels;
  bytesLoaded: number;
  /** Correction versions, dirty state, rows in edit mode (null until the measurements load). */
  corrections: CorrectionsSummary | null;
}

type Listener = () => void;

export class TheaterSession {
  readonly viewer: Viewer;
  readonly manifest: Manifest;
  volume: VolumeObject | null = null;
  surfaces = new SurfaceSet();
  prediction: PredictionObject | null = null;
  mpr: MprSet | null = null;
  grid: VolumeGrid | null = null;
  state: TheaterState;
  version = 0;
  private listeners = new Set<Listener>();
  private volumeCache = new ByteBudgetCache<{ byteLength: number; texture: THREE.Data3DTexture; data: VolumeData }>(1024 * 1024 * 1024);
  private textures = new Map<number, THREE.Data3DTexture>();
  private labelTexture: THREE.Data3DTexture | null = null;
  private isolationTextures = new Map<string, THREE.Data3DTexture>();
  /** Leaflet fields by frame, read on demand while a leaflet preset is up (never evicted, like the CT frames). */
  private cuspFields = new Map<number, CuspFrame>();
  private cuspLoads = new Map<number, Promise<CuspFrame>>();
  /** Serial of the latest bind request; an older read that lands later never binds. */
  private cuspRequest = 0;
  /** Clean bundles by frame, read on demand while Clean Root / Emptied is up (never evicted, like the CT frames). */
  private cleanFrames = new Map<number, CleanFrame>();
  private cleanLoads = new Map<number, Promise<CleanFrame>>();
  private cleanRequest = 0;
  private cineTimer: number | null = null;
  private rootCropId = 'root-crop';
  private viewClipId = 'view-clip';
  private rootClipStjId = 'root-clip-stj';
  private rootClipVbrId = 'root-clip-vbr';
  private thumbnailDone = false;
  private disposed = false;
  /** HU encoded as 0 and 1 in the 8-bit volume (asset meta `intensity`). */
  huRange: [number, number] = [-1024, 3071];
  private histogramTimer: number | null = null;
  /** The correction system of this run (null until the measurements load). */
  corrections: Corrections | null = null;
  private editController: EditController | null = null;
  private handleSet = new HandleSet();
  private rebuildTimer: number | null = null;

  constructor(viewer: Viewer, manifest: Manifest) {
    this.viewer = viewer;
    this.manifest = manifest;
    this.state = {
      loading: 'Loading…',
      error: null,
      surfaceErrors: [],
      presetName: DEFAULT_PRESET,
      availablePresets: [],
      window: LOAD_DEFAULTS.window,
      boxOpacity: LOAD_DEFAULTS.boxOpacity,
      cameraClip: LOAD_DEFAULTS.cameraClip,
      lighting: false,
      renderMode: 'dvr',
      samplingMultiplier: LOAD_DEFAULTS.samplingMultiplier,
      cubic: false,
      reduceWhileMoving: viewer.reduceWhileInteracting,
      tf: tfEdit.clone(PRESETS.find((p) => p.name === DEFAULT_PRESET)!.tf),
      tfDomain: { ...NEUTRAL_DOMAIN },
      tfShiftHu: 0,
      tfStretch: 1,
      tfEdited: false,
      gradientThreshold: LOAD_DEFAULTS.minGradient,
      leafletOverlay: false,
      leafletStatus: 'off',
      leafletFill: LEAFLET_DEFAULT_FILL,
      leafletOpacity: LEAFLET_DEFAULT_OPACITY,
      cleanRootStatus: 'off',
      rawSetOpacity: 1,
      cleanSetOpacity: 1,
      mprLeaflets: false,
      histogram: null,
      histogramRegionOnly: true,
      histogramCaption: '',
      orthographic: false,
      showVolume: true,
      showLabels: true,
      hideAll: false,
      frame: manifest.primary_frame ?? 0,
      pendingFrame: null,
      frameError: null,
      loadedFrames: new Set(),
      playing: false,
      fps: 7,
      commissureView: null,
      mprWindow: [0, 1],
      mprGain: 0,
      mprContrast: 1,
      mprZoom: 0.5,
      mprOffsets: { x: 0, y: 0, z: 0 },
      mprOverlays: true,
      mprLabels: false,
      mprPlanesIn3D: { x: false, y: false, z: false },
      rows: new Map(),
      axisLabels: DEFAULT_AXIS_LABELS,
      bytesLoaded: 0,
      corrections: null,
    };
    viewer.onRendered = (id) => {
      if (id === 'main') void this.maybeCaptureThumbnail();
    };
    if (import.meta.env.DEV) (window as unknown as { __valvia?: unknown }).__valvia = { session: this, viewer };
    viewer.controls.chords = {
      onWindowLevel: (dx, dy, phase) => this.windowLevelChord(dx, dy, phase),
      onCameraClip: (dy, phase) => this.cameraClipChord(dy, phase),
    };
  }

  // ------------------------------------------------------------ subscription
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private emit(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  private set(patch: Partial<TheaterState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  // ------------------------------------------------------------ loading
  async load(): Promise<void> {
    try {
      const m = this.manifest;
      const ct = m.assets.find((a) => a.kind === 'ct' && (a.frame_index ?? 0) === this.state.frame) ?? m.assets.find((a) => a.kind === 'ct');
      if (!ct || !ct.dims || !ct.voxel_size) throw new Error('run has no CT volume');
      this.grid = { dims: ct.dims, voxelSize: ct.voxel_size };
      const intensity = ct.meta?.intensity as { normalized_min?: number; normalized_max?: number } | undefined;
      if (intensity?.normalized_min !== undefined && intensity?.normalized_max !== undefined) this.huRange = [intensity.normalized_min, intensity.normalized_max];
      this.set({ loading: 'Loading CT volume…', axisLabels: axisLabelsFromOrientation(ct.meta?.patient_orientation) });
      await this.loadStateFiles();

      const [tex, surfacesDone, rows] = await Promise.all([
        this.volumeTexture(ct),
        this.surfaces.load(
          m.assets.filter((a) => a.kind === 'surface' && !a.flags.includes('frame_copy')),
          () => this.set({ bytesLoaded: this.state.bytesLoaded }),
        ),
        this.loadMeasurements(),
      ]);
      void surfacesDone;
      if (this.disposed) return;
      if (this.surfaces.failed.length) this.set({ surfaceErrors: this.surfaces.failed.map((f) => f.name) });

      const preset = PRESETS.find((p) => p.name === DEFAULT_PRESET)!;
      this.volume = new VolumeObject(tex, this.grid, preset.tf);
      this.volume.mesh.layers.set(LAYER_MAIN);
      this.viewer.root.add(this.volume.mesh);
      this.viewer.volume = this.volume;
      this.viewer.root.add(this.surfaces.group);

      if (rows) {
        const corr = new Corrections(m.run_id, m.project_id, rows, this.activeConfiguration());
        const centerline = this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'));
        if (centerline?.vertices) corr.pipelineCenterline = this.orderedCenterline(centerline.vertices, parseMeasurements(rows, this.activeConfiguration()));
        const aortaCurve = this.surfaces.find((i) => i.isLine && !i.name.toLowerCase().startsWith('centerline'));
        if (aortaCurve?.vertices) corr.aortaCurve = aortaCurve.vertices;
        corr.meshesFor = (which) => this.contourMeshes(which);
        corr.surfaceRaycast = (o, d, max) => this.surfaceRaycastScene(o, d, max);
        corr.onChange = () => this.onCorrectionsChange();
        this.corrections = corr;
        this.viewer.root.add(this.handleSet.group);
        this.buildPrediction();
        this.editController = new EditController(this, this.viewer.views.get('main')!.element);
        this.set({ corrections: corr.summary() });
      }

      this.mpr = new MprSet(this.viewer, this.volume, this.grid, this.huRange);
      this.viewer.scene.add(this.mpr.group);
      this.seedMpr();
      this.set({ mprWindow: this.mpr.window });
      await this.loadLabelOverlay(ct);

      this.set({ availablePresets: this.availablePresets().map((p) => p.name) });
      this.applyInventoryDefaults();
      await this.loadIsolationMasks();
      this.applyPreset(DEFAULT_PRESET);
      this.frameCamera();
      this.set({ loading: null });
      this.viewer.invalidate();
      // the newest saved version loads on open, as on the desktop
      void this.corrections?.loadNewest();
    } catch (e) {
      console.error(e);
      this.set({ loading: null, error: (e as Error).message });
    }
  }

  private async volumeTexture(asset: AssetEntry): Promise<THREE.Data3DTexture> {
    const frame = asset.frame_index ?? 0;
    const cached = this.textures.get(frame);
    if (cached) return cached;
    const hit = this.volumeCache.get(asset.key);
    if (hit) return hit.texture;
    const data = await loadVolume(asset);
    const texture = makeData3DTexture(data);
    this.volumeCache.set(asset.key, { byteLength: data.bytes.byteLength, texture, data }, new Set([asset.key]));
    this.textures.set(frame, texture);
    this.set({ bytesLoaded: this.state.bytesLoaded + data.bytes.byteLength, loadedFrames: new Set(this.textures.keys()) });
    return texture;
  }

  private async loadMeasurements(): Promise<MeasurementRow[] | null> {
    const asset = this.manifest.assets.find((a) => a.kind === 'prediction');
    if (!asset) return null;
    const res = await fetch(asset.url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(`measurements fetch ${res.status}`);
    return (await res.json()) as MeasurementRow[];
  }

  private activeConfiguration(): string | null {
    // valve_configuration.json shipped with the run (read-only)
    return (this.stateFile('valve_configuration.json') as { active?: string } | null)?.active ?? null;
  }

  private stateFiles = new Map<string, unknown>();
  private stateFile(name: string): unknown {
    return this.stateFiles.get(name) ?? null;
  }

  async loadStateFiles(): Promise<void> {
    const files = this.manifest.assets.filter((a) => a.kind === 'state');
    await Promise.all(
      files.map(async (a) => {
        try {
          const res = await fetch(a.url, { mode: 'cors', credentials: 'omit' });
          if (res.ok) this.stateFiles.set(a.name.split('/').pop() ?? a.name, await res.json());
        } catch {
          /* optional */
        }
      }),
    );
  }

  private async loadLabelOverlay(ct: AssetEntry): Promise<void> {
    const overlay = this.manifest.assets.find((a) => a.kind === 'overlay' && (a.frame_index ?? 0) === (ct.frame_index ?? 0));
    if (!overlay || !this.mpr) return;
    try {
      const data = await loadVolume(overlay);
      this.labelTexture = makeData3DTexture(data, THREE.NearestFilter);
      const labels = (overlay.meta?.labels ?? {}) as Record<string, string>;
      const lut = new Map<number, Rgba>();
      for (const [id, name] of Object.entries(labels)) {
        const key = name === 'NSinus' ? 'NSinus' : name === 'RSinus' ? 'RSinus' : name === 'LSinus' ? 'LSinus' : null;
        lut.set(Number(id), key ? DEFAULT_COLORS[key] : [0.8, 0.8, 0.2, 0.4]);
      }
      this.labelLut = lut;
      this.set({ bytesLoaded: this.state.bytesLoaded + data.bytes.byteLength });
    } catch (e) {
      console.warn('label overlay unavailable', e);
    }
  }
  private labelLut = new Map<number, Rgba>();

  /** fields/isolation*.r8: the aorta (+ LVOT) mask on a 192^3 uvw grid, pristine and grown. */
  private async loadIsolationMasks(): Promise<void> {
    const masks = this.manifest.assets.filter((a) => a.kind === 'field' && typeof a.meta?.field === 'string' && (a.meta.field as string).startsWith('isolation'));
    await Promise.all(
      masks.map(async (a) => {
        try {
          const data = await loadVolume(a);
          this.isolationTextures.set(a.meta!.field as string, makeData3DTexture(data));
          this.set({ bytesLoaded: this.state.bytesLoaded + data.bytes.byteLength });
        } catch (e) {
          console.warn('isolation mask unavailable', e);
        }
      }),
    );
  }

  private isolationFor(presetName: string): THREE.Data3DTexture | null {
    if (presetName === 'Clean Root') return this.isolationTextures.get('isolation_grow3') ?? this.isolationTextures.get('isolation') ?? null;
    if (presetName === 'Endocast') return this.isolationTextures.get('isolation') ?? null;
    return null;
  }

  /** Root Centroid line ordered inferior -> superior (STJ above VBR, else commissures above nadirs). */
  private orderedCenterline(vertices: THREE.Vector3[], prediction: ReturnType<typeof parseMeasurements>): THREE.Vector3[] {
    if (vertices.length < 2) return vertices;
    const dir = vertices[vertices.length - 1].clone().sub(vertices[0]);
    const vbr = prediction.planes.get('VBR')?.center ?? null;
    const stjPts = prediction.rings.get('STJ');
    let sign = 0;
    if (vbr && stjPts?.length) {
      const stjC = stjPts.reduce((a, b) => a.add(b), new THREE.Vector3()).multiplyScalar(1 / stjPts.length);
      sign = Math.sign(stjC.sub(vbr).dot(dir));
    }
    if (sign === 0) {
      const comm = prediction.points.filter((p) => p.structure === 'Commissure');
      const nad = prediction.points.filter((p) => p.structure === 'Nadir');
      if (comm.length && nad.length) {
        const mc = comm.reduce((a, b) => a.add(b.position), new THREE.Vector3()).multiplyScalar(1 / comm.length);
        const mn = nad.reduce((a, b) => a.add(b.position), new THREE.Vector3()).multiplyScalar(1 / nad.length);
        sign = Math.sign(mc.sub(mn).dot(dir));
      }
    }
    if (sign === 0 && prediction.planes.get('VBR')?.normal) sign = Math.sign(prediction.planes.get('VBR')!.normal!.dot(dir));
    return sign < 0 ? vertices.slice().reverse() : vertices;
  }

  // ------------------------------------------------------------ MPR seed
  private seedMpr(): void {
    if (!this.mpr || !this.grid) return;
    const pred = this.prediction;
    const centerline = this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'));
    let center: THREE.Vector3;
    let tangent: THREE.Vector3;
    const vbr = pred?.vbrPlane ?? null;
    if (centerline?.vertices && centerline.vertices.length >= 2) {
      const pts = centerline.vertices;
      // the point closest to the VBR plane (centerline slider 0 = VBR offset)
      let bi = 0;
      if (vbr) {
        let best = Infinity;
        pts.forEach((q, i) => {
          const d = Math.abs(q.clone().sub(vbr.center).dot(vbr.normal));
          if (d < best) {
            best = d;
            bi = i;
          }
        });
      }
      center = pts[bi].clone();
      tangent = pts[Math.min(bi + 1, pts.length - 1)].clone().sub(pts[Math.max(bi - 1, 0)]).normalize();
      if (vbr && tangent.dot(vbr.normal) < 0 && pred) {
        // keep the tangent pointing superior: compare with STJ direction
        const stj = pred.ringMetrics.get('STJ')?.centroid;
        if (stj && stj.clone().sub(vbr.center).dot(tangent) < 0) tangent.negate();
      }
    } else if (vbr) {
      center = vbr.center.clone();
      tangent = vbr.normal.clone();
      const stj = pred?.ringMetrics.get('STJ')?.centroid;
      if (stj && stj.clone().sub(vbr.center).dot(tangent) < 0) tangent.negate();
    } else {
      const e = this.grid;
      center = new THREE.Vector3((e.dims[0] * e.voxelSize[0]) / 2, (e.dims[1] * e.voxelSize[1]) / 2, (e.dims[2] * e.voxelSize[2]) / 2);
      tangent = new THREE.Vector3(0, 1, 0);
    }
    this.mpr.seedTriad(center, tangent, vbr?.normal ?? null, vbr?.center ?? null);
    this.refreshMprOverlays();
  }

  private refreshMprOverlays(): void {
    if (!this.mpr) return;
    const pred = this.prediction;
    const curves: MprSet['curves'] = [];
    const dots: MprSet['dots'] = [];
    if (pred) {
      const p = pred.prediction;
      const rowVisible = (key: string) => this.state.rows.get(key)?.visible ?? false;
      const colorOf = (key: string, fallback: Rgba) => this.state.rows.get(key)?.color ?? fallback;
      const vbr = p.rings.get('VBR');
      if (vbr && rowVisible('VbrEllipse')) curves.push({ key: 'VBR', points: vbr, closed: true, color: withAlpha(colorOf('VbrEllipse', [1, 0.55, 0, 0.9]), 0.9), fade: true, cull: false });
      const stj = p.rings.get('STJ');
      if (stj && rowVisible('StjContour')) curves.push({ key: 'STJ', points: stj, closed: true, color: withAlpha(colorOf('StjContour', [0.65, 0.35, 0.9, 0.9]), 0.9), fade: false, cull: true });
      const sov = p.rings.get('SOV');
      if (sov && rowVisible('SovContour')) curves.push({ key: 'SOV', points: sov, closed: true, color: withAlpha(colorOf('SovContour', [0.97, 0.29, 0.64, 0.9]), 0.9), fade: true, cull: true });
      const curveRow = (key: string, structure: string) => {
        const pts = p.curves.get(structure);
        if (pts && rowVisible(key)) curves.push({ key, points: pts, closed: false, color: colorOf(key, DEFAULT_COLORS[key]), fade: true, cull: true });
      };
      curveRow('NGeometricHeight', 'NGeometricHeight');
      curveRow('RGeometricHeight', 'RGeometricHeight');
      curveRow('LGeometricHeight', 'LGeometricHeight');
      curveRow('RnLnFreeMargin', 'RN_LN_FreeMargin');
      curveRow('RnRlFreeMargin', 'RN_RL_FreeMargin');
      curveRow('RlLnFreeMargin', 'RL_LN_FreeMargin');
      const centerline = this.centerlinePoints();
      if (centerline.length >= 2 && rowVisible('Centerline')) curves.push({ key: 'Centerline', points: centerline, closed: false, color: colorOf('Centerline', DEFAULT_COLORS.Centerline), fade: true, cull: true });
      const dot = (key: string, point: THREE.Vector3 | undefined | null, fallback: Rgba) => {
        if (point && rowVisible(key)) dots.push({ key, point, color: colorOf(key, fallback) });
      };
      dot('LNadir', pred.nadirs.L, DEFAULT_COLORS.LNadir);
      dot('NNadir', pred.nadirs.N, DEFAULT_COLORS.NNadir);
      dot('RNadir', pred.nadirs.R, DEFAULT_COLORS.RNadir);
      dot('RnCommissure', pred.commissures.RN, [1, 0, 0.8, 0.9]);
      dot('RlCommissure', pred.commissures.RL, [1, 0, 0.8, 0.9]);
      dot('LnCommissure', pred.commissures.LN, [1, 0, 0.8, 0.9]);
      dot('CentralCoaptationPoint', pred.coaptation, [1, 0, 0.8, 1]);
      dot('CommissuralAnglePoint', pred.wedgeApex, [0.65, 0.35, 0.9, 1]);
      const ost = (key: string, name: string, c: Rgba) => {
        const pt = p.points.find((x) => x.structure === 'Ostium' && x.name === name);
        dot(key, pt?.position, c);
      };
      ost('LcaCoronaryOstia', 'Left Coronary Ostium', [0.6, 0.1, 0.1, 0.9]);
      ost('RcaCoronaryOstia', 'Right Coronary Ostium', [0.8, 0.6, 0, 0.9]);
    }
    this.mpr.curves = curves;
    this.mpr.dots = dots;
    this.mpr.showOverlays = this.state.mprOverlays && !this.state.hideAll;
    this.mpr.invalidate();
  }

  // ------------------------------------------------------------ corrections
  /** (Re)build the prediction overlays from the corrected rows, keeping the rows' eye and
   * colour state and refreshing their values. */
  private buildPrediction(): void {
    const corr = this.corrections;
    if (!corr) return;
    const prediction = parseMeasurements(corr.rows(), this.activeConfiguration());
    const line = corr.centerlineOverride() ?? corr.pipelineCenterline;
    const old = this.prediction;
    if (old) {
      this.viewer.root.remove(old.group);
      old.dispose();
    }
    const next = new PredictionObject(prediction, line ? { points: line } : null);
    next.group.layers.set(LAYER_OVERLAY);
    next.group.visible = !this.state.hideAll;
    next.setLabelsVisible(this.state.showLabels);
    this.viewer.root.add(next.group);
    this.prediction = next;
    const item = this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'));
    if (item) this.surfaces.setPolyline(item, corr.centerlineOverride());
    if (old) {
      const rows = new Map(this.state.rows);
      for (const row of rows.values()) {
        const entry = next.entries.get(row.key);
        if (!entry) continue;
        rows.set(row.key, { ...row, value: entry.value, info: entry.info });
        next.setVisible(row.key, row.visible);
        next.setColor(row.key, row.color);
      }
      this.state = { ...this.state, rows };
      this.emit();
    }
    this.handleSet.sync(corr.contourHandleSpecs());
    this.editController?.refreshAll();
    this.refreshMprOverlays();
    this.refreshCuspPlane();
    this.refreshRootClipPlanes();
    this.viewer.invalidate('main');
  }

  /** Every drag handle, or only those of rows in edit mode. */
  editableHandles(onlyEditing: boolean): { key: string; mesh: THREE.Mesh }[] {
    const corr = this.corrections;
    const pred = this.prediction;
    const out: { key: string; mesh: THREE.Mesh }[] = [];
    if (!corr || !pred) return out;
    for (const [key, mesh] of pred.handles) {
      if (!corr.hasLandmark(key) || !pred.entries.get(key)?.visible) continue;
      if (onlyEditing && !corr.editing.has(key)) continue;
      out.push({ key, mesh });
    }
    for (const [key, mesh] of this.handleSet.handles) out.push({ key, mesh }); // drawn only in edit mode
    return out;
  }

  /** Dispatch a drag to the landmark or the contour handle it belongs to. */
  moveHandle(key: string, scene: THREE.Vector3, live: boolean): void {
    const corr = this.corrections;
    if (!corr) return;
    if (key.startsWith('contour:')) {
      const [, ringKey, which] = key.split(':');
      corr.moveContourHandle(ringKey, which === 'center' ? 'center' : Number(which), scene, live);
    } else corr.moveLandmark(key, scene, live);
  }

  setHandleVisual(key: string, state: { hover: boolean; dragging: boolean }): void {
    if (key.startsWith('contour:')) this.handleSet.setState(key, state);
    else this.prediction?.setHandleState(key, { editing: !!this.corrections?.editing.has(key), ...state });
  }

  /** The meshes a contour re-slices: STJ the aorta; SOV and the ascending aorta the Aorta/LV
   * (full tube) when the run ships it, else the aorta. */
  private contourMeshes(which: ContourDef['mesh']): THREE.Mesh[] {
    const pick = (keys: string[]) => {
      for (const k of keys) {
        const item = this.surfaces.items.find((i) => i.style?.colorKey === k && !i.isLine);
        if (item) {
          const meshes: THREE.Mesh[] = [];
          item.object.traverse((o) => {
            if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh);
          });
          if (meshes.length) return meshes;
        }
      }
      return [];
    };
    if (which === 'stj') return pick(['AorticRoot', 'AortaLvSegmentation']);
    return pick(['AortaLvSegmentation', 'AorticRoot']);
  }

  /** Scene-space raycast against the aorta meshes (hits in scene mm, nearest first). */
  private surfaceRaycastScene(origin: THREE.Vector3, dir: THREE.Vector3, maxDistanceMm: number): THREE.Vector3[] {
    const meshes = this.contourMeshes('stj');
    if (!meshes.length) return [];
    this.viewer.root.updateMatrixWorld();
    const o = this.sceneToWorld(origin);
    const d = dir.clone().transformDirection(this.viewer.root.matrixWorld).normalize();
    const rc = new THREE.Raycaster(o, d, 0, maxDistanceMm / MM_PER_UNIT);
    rc.layers.enableAll();
    return rc.intersectObjects(meshes, false).map((h) => this.worldToScene(h.point));
  }

  private onCorrectionsChange(): void {
    const corr = this.corrections;
    if (!corr) return;
    this.set({ corrections: corr.summary() });
    if (this.rebuildTimer) return;
    this.rebuildTimer = window.setTimeout(() => {
      this.rebuildTimer = null;
      if (!this.disposed) this.buildPrediction();
    }, 0);
  }

  moveLandmark(key: string, scene: THREE.Vector3, live: boolean): void {
    this.corrections?.moveLandmark(key, scene, live);
  }

  setLandmarkEditing(key: string, on: boolean): void {
    this.corrections?.setEditing(key, on);
    this.editController?.refreshAll();
    this.viewer.invalidate('main');
  }

  clearLandmarkEditing(): void {
    this.corrections?.clearEditing();
    this.editController?.refreshAll();
    this.viewer.invalidate('main');
  }

  resetLandmark(key: string): void {
    this.corrections?.resetLandmark(key);
  }

  resetRow(key: string): void {
    this.corrections?.resetRow(key);
  }

  resetMargin(structure: string): void {
    this.corrections?.resetMargin(structure);
  }

  setSnapMode(mode: SnapMode): void {
    this.corrections?.setSnapMode(mode);
  }

  saveCorrections(): Promise<void> {
    return this.corrections?.save() ?? Promise.resolve();
  }

  saveCorrectionsAs(label: string): Promise<boolean> {
    return this.corrections?.saveAs(label) ?? Promise.resolve(false);
  }

  activateCorrections(slug: string | null): Promise<void> {
    return this.corrections?.activate(slug) ?? Promise.resolve();
  }

  deleteCorrections(): Promise<void> {
    return this.corrections?.deleteActive() ?? Promise.resolve();
  }

  /** The Root Centroid drawn now (scene mm): a rebuild after an edit, else the pipeline's. */
  centerlinePoints(): THREE.Vector3[] {
    const corr = this.corrections;
    if (corr) return corr.centerlineOverride() ?? corr.pipelineCenterline ?? [];
    return this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'))?.vertices ?? [];
  }

  worldToScene(world: THREE.Vector3): THREE.Vector3 {
    this.viewer.root.updateMatrixWorld();
    return world.clone().applyMatrix4(new THREE.Matrix4().copy(this.viewer.root.matrixWorld).invert());
  }

  sceneToWorld(scene: THREE.Vector3): THREE.Vector3 {
    this.viewer.root.updateMatrixWorld();
    return scene.clone().applyMatrix4(this.viewer.root.matrixWorld);
  }

  /** The landmark dot under a slice-pane pixel (10 px), optionally only rows in edit mode. */
  landmarkAtPanePixel(pane: PaneId, px: number, py: number, onlyEditing: boolean): string | null {
    const mpr = this.mpr;
    const corr = this.corrections;
    if (!mpr || !corr) return null;
    let best: string | null = null;
    let bestD = 10;
    for (const dot of mpr.dots) {
      if (!corr.hasLandmark(dot.key) || (onlyEditing && !corr.editing.has(dot.key))) continue;
      const p = mpr.worldToPixel(pane, this.sceneToWorld(dot.point));
      if (!p) continue;
      const d = Math.hypot(p.x - px, p.y - py);
      if (d < bestD) {
        bestD = d;
        best = dot.key;
      }
    }
    return best;
  }

  /** Snap targets along a world ray for a dragged landmark (VolumetricViewSnapMode): the
   * nearest of the segmentation-surface hit and the first visible voxel, per the snap mode. */
  snapAlongRay(ray: THREE.Ray, how: 'mode' | 'surface-only'): THREE.Vector3 | null {
    const mode = this.corrections?.snapMode ?? 'both';
    if (mode === 'off') return null;
    let surface: THREE.Vector3 | null = null;
    let volume: THREE.Vector3 | null = null;
    if (mode === 'surface' || mode === 'both') surface = this.surfaceHit(ray, how);
    if (how === 'mode' && (mode === 'volumetric' || mode === 'both')) volume = this.volumetricHit(ray);
    if (surface && volume) return ray.origin.distanceToSquared(surface) <= ray.origin.distanceToSquared(volume) ? surface : volume;
    return surface ?? volume;
  }

  /** Raycast against the cusp and aorta meshes (hidden ones too, like the desktop colliders). */
  private surfaceHit(ray: THREE.Ray, _how: 'mode' | 'surface-only'): THREE.Vector3 | null {
    const roots = new Set(['NSinus', 'RSinus', 'LSinus', 'AorticRoot', 'AortaLvSegmentation']);
    const meshes: THREE.Object3D[] = [];
    for (const item of this.surfaces.items) if (item.style && roots.has(item.style.colorKey)) meshes.push(item.object);
    if (!meshes.length) return null;
    const rc = new THREE.Raycaster(ray.origin, ray.direction);
    rc.layers.enableAll();
    const hits = rc.intersectObjects(meshes, true);
    return hits.length ? hits[0].point.clone() : null;
  }

  /** VolumetricVisibleSnapUtility: replay the ray march on the CPU and stop at the first voxel
   * whose transfer-function opacity passes 0.15 inside the window, the cutout box and the
   * isolation mask, beyond the camera clip. */
  private volumetricHit(ray: THREE.Ray): THREE.Vector3 | null {
    const v = this.volume;
    const grid = this.grid;
    if (!v || !grid) return null;
    const ct = this.manifest.assets.find((a) => a.kind === 'ct' && (a.frame_index ?? 0) === this.state.frame);
    const data = ct ? this.volumeCache.get(ct.key)?.data : undefined;
    if (!data) return null;
    const [W, H, D] = data.dims;
    const vs = data.voxelSize;
    this.viewer.root.updateMatrixWorld();
    const inv = new THREE.Matrix4().copy(this.viewer.root.matrixWorld).invert();
    const origin = ray.origin.clone().applyMatrix4(inv);
    const dir = ray.direction.clone().transformDirection(inv).normalize();
    const worldPerMm = 1 / MM_PER_UNIT;
    // slab test against the box [-vs/2, (N-1/2) vs] in scene mm
    let t0 = 0;
    let t1 = Infinity;
    const mins = [-vs[0] / 2, -vs[1] / 2, -vs[2] / 2];
    const maxs = [(W - 0.5) * vs[0], (H - 0.5) * vs[1], (D - 0.5) * vs[2]];
    const o = [origin.x, origin.y, origin.z];
    const d = [dir.x, dir.y, dir.z];
    for (let a = 0; a < 3; a++) {
      if (Math.abs(d[a]) < 1e-9) {
        if (o[a] < mins[a] || o[a] > maxs[a]) return null;
        continue;
      }
      let ta = (mins[a] - o[a]) / d[a];
      let tb = (maxs[a] - o[a]) / d[a];
      if (ta > tb) [ta, tb] = [tb, ta];
      t0 = Math.max(t0, ta);
      t1 = Math.min(t1, tb);
    }
    if (t1 <= t0) return null;
    const clipMm = v.cameraClipDistance / worldPerMm;
    t0 = Math.max(t0, clipMm);
    const [wMin, wMax] = this.state.window;
    const tf = v.bakedTf;
    const crop = v.getCrossSection(this.rootCropId);
    const box = crop?.enabled ? { c: crop.object.position, s: crop.object.scale } : null;
    const mask = this.isolationFor(this.state.presetName);
    const md = mask ? (mask.image as { data: Uint8Array; width: number; height: number; depth: number }) : null;
    const step = 0.5 * Math.min(vs[0], vs[1], vs[2]);
    for (let t = t0; t <= t1; t += step) {
      const x = o[0] + d[0] * t;
      const y = o[1] + d[1] * t;
      const z = o[2] + d[2] * t;
      const i = Math.round(x / vs[0]);
      const j = Math.round(y / vs[1]);
      const k = Math.round(z / vs[2]);
      if (i < 0 || j < 0 || k < 0 || i >= W || j >= H || k >= D) continue;
      if (box && (Math.abs(x - box.c.x) > box.s.x / 2 || Math.abs(y - box.c.y) > box.s.y / 2 || Math.abs(z - box.c.z) > box.s.z / 2)) continue;
      if (md) {
        const mi = Math.min(md.width - 1, (((i + 0.5) / W) * md.width) | 0);
        const mj = Math.min(md.height - 1, (((j + 0.5) / H) * md.height) | 0);
        const mk = Math.min(md.depth - 1, (((k + 0.5) / D) * md.depth) | 0);
        if (md.data[mi + md.width * (mj + md.height * mk)] < 128) continue;
      }
      const density = data.bytes[(i + W * (j + H * k)) * data.channels] / 255;
      if (density < wMin || density > wMax) continue;
      const alpha = tf[Math.round(density * 1023) * 4 + 3];
      if (alpha > 0.15) return this.sceneToWorld(new THREE.Vector3(x, y, z));
    }
    return null;
  }

  // ------------------------------------------------------------ inventory
  private applyInventoryDefaults(): void {
    const overrides = readInventoryVisibility(this.stateFile('inventory_visibility.json'));
    const rows = new Map<string, InventoryRowState>();
    for (const section of SECTIONS) {
      for (const row of section.rows) {
        const entry = this.prediction?.entries.get(row.key);
        const surface = this.surfaceForRow(row.key);
        const present = !!entry || !!surface;
        const defaultVisible = !DEFAULT_OFF.has(row.key);
        const visible = overrides.has(row.key) ? overrides.get(row.key)! : defaultVisible;
        const color: Rgba = entry?.color ?? surface?.color ?? DEFAULT_COLORS[row.key] ?? [1, 1, 1, 1];
        rows.set(row.key, { key: row.key, present, visible: present && visible, color, value: entry?.value ?? '-', info: entry?.info ?? [] });
      }
    }
    this.state = { ...this.state, rows };
    for (const r of rows.values()) this.applyRow(r);
    this.emit();
    this.refreshMprOverlays();
  }

  private surfaceForRow(key: string): SurfaceItem | undefined {
    if (key === 'Centerline') return this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'));
    if (key === 'AortaCurve') return this.surfaces.find((i) => i.isLine && !i.name.toLowerCase().startsWith('centerline'));
    if (!SEGMENTATION_KEYS.has(key)) return undefined;
    return this.surfaces.find((i) => i.style?.colorKey === key);
  }

  private applyRow(row: InventoryRowState): void {
    const entry = this.prediction?.entries.get(row.key);
    if (entry) {
      this.prediction!.setVisible(row.key, row.visible);
      this.prediction!.setColor(row.key, row.color);
    }
    const surface = this.surfaceForRow(row.key);
    if (surface) {
      surface.object.visible = row.visible;
      this.surfaces.setColor(surface, row.color);
    }
  }

  /** Replace the rows map (a new Map and a new state object, so the React store hook sees a
   * changed snapshot), apply every changed row to the scene and refresh the overlays. */
  private updateRows(update: (row: InventoryRowState) => InventoryRowState | null): void {
    const rows = new Map(this.state.rows);
    for (const row of this.state.rows.values()) {
      const next = update(row);
      if (!next || next === row) continue;
      rows.set(row.key, next);
      this.applyRow(next);
    }
    this.set({ rows });
    this.refreshMprOverlays();
    this.viewer.invalidate('main');
  }

  setRowVisible(key: string, visible: boolean): void {
    this.updateRows((row) => (row.key === key && row.present && row.visible !== visible ? { ...row, visible } : null));
  }

  setRowColor(key: string, color: Rgba): void {
    this.updateRows((row) => (row.key === key ? { ...row, color } : null));
  }

  setSectionVisible(title: string, visible: boolean): void {
    const keys = new Set(SECTIONS.find((s) => s.title === title)?.rows.map((r) => r.key) ?? []);
    this.updateRows((row) => (keys.has(row.key) && row.present ? { ...row, visible } : null));
  }

  /** Item star: hide everything, show only this row. Section star: only this section. */
  isolateRow(key: string): void {
    this.updateRows((row) => ({ ...row, visible: row.present && row.key === key }));
  }

  isolateSection(title: string): void {
    const keys = new Set(SECTIONS.find((s) => s.title === title)?.rows.map((r) => r.key) ?? []);
    this.updateRows((row) => ({ ...row, visible: row.present && keys.has(row.key) }));
  }

  showHideAll(): void {
    const anyVisible = [...this.state.rows.values()].some((r) => r.visible);
    this.updateRows((row) => ({ ...row, visible: row.present && !anyVisible }));
  }

  showDefaultInventory(): void {
    this.stateFiles.delete('inventory_visibility.json');
    this.applyInventoryDefaults();
    this.viewer.invalidate('main');
  }

  setHideAll(on: boolean): void {
    this.set({ hideAll: on });
    if (this.prediction) this.prediction.group.visible = !on;
    this.surfaces.group.visible = !on;
    this.refreshMprOverlays();
    this.viewer.invalidate('main');
  }

  setShowLabels(on: boolean): void {
    this.set({ showLabels: on });
    this.prediction?.setLabelsVisible(on);
    this.viewer.invalidate('main');
  }

  // ------------------------------------------------------------ presets and volume controls
  availablePresets(): PresetDefinition[] {
    const has = (kind: string) => this.manifest.assets.some((a) => a.kind === kind);
    const hasField = (name: string) => this.manifest.assets.some((a) => a.kind === 'field' && a.meta?.field === name);
    return PRESETS.filter((p) => {
      const missing = p.requires.filter((r) => (r === 'clean_ct' ? !has('clean_ct') : r === 'cusp_field' ? !hasField('leaflet') && !hasField('leaflet_valvia') : r === 'isolation_mask' ? !hasField('isolation') : false));
      return missing.length === 0 || p.whenMissing === 'fallback';
    });
  }

  applyPreset(name: string): void {
    const preset = PRESETS.find((p) => p.name === name);
    const v = this.volume;
    if (!preset || !v) return;
    v.setDomain(0, 1);
    v.setRenderMode('dvr');
    v.setVisibilityWindow(preset.window[0], preset.window[1]);
    const lighting = preset.lighting === 'keep' ? this.state.lighting : preset.lighting;
    v.setLightingEnabled(lighting);
    v.setGradientLightingThreshold(preset.lightingThreshold[0], preset.lightingThreshold[1]);
    const tf = tfEdit.clone(preset.tf);
    v.setTransferFunction(tf);
    v.setCameraClipDistance(preset.cameraClip);
    v.setBoxOpacity(preset.boxOpacity);
    const q = qualityOverride(preset.samplingMultiplier, preset.cubic);
    this.viewer.restQuality = q;
    this.viewer.applyQuality();
    this.setRootCrop(preset.rootCrop);
    v.setIsolationMask(this.isolationFor(name), 0.5);
    // The leaflet look is re-pushed on every tile select, as on the desktop.
    v.setLeafletLook({ fill: LEAFLET_DEFAULT_FILL, emphasis: LEAFLET_DEFAULT_EMPHASIS, opacity: LEAFLET_DEFAULT_OPACITY, color: preset.leafletColor === 'tf' ? null : preset.leafletColor });
    this.mpr?.setLeafletFill(LEAFLET_DEFAULT_FILL);
    // Every set fully rendered on (re)selecting the tile; the transparency sliders fade them from here.
    v.setRenderSetOpacities(1, 1);
    this.set({
      leafletOverlay: preset.leafletOverlay,
      leafletFill: LEAFLET_DEFAULT_FILL,
      leafletOpacity: LEAFLET_DEFAULT_OPACITY,
      rawSetOpacity: 1,
      cleanSetOpacity: 1,
      presetName: name,
      window: preset.window,
      boxOpacity: preset.boxOpacity,
      cameraClip: preset.cameraClip,
      lighting,
      renderMode: 'dvr',
      samplingMultiplier: q.samplingMultiplier,
      cubic: q.cubic,
      tf,
      tfDomain: v.domain,
      tfShiftHu: 0,
      tfStretch: 1,
      tfEdited: false,
    });
    void this.bindLeafletField();
    void this.bindCleanVolume();
    this.requestHistogram();
    this.viewer.invalidate('main');
  }

  // ------------------------------------------------------------ clean root / emptied
  /**
   * Bind the active frame's clean bundle the way the preset wants it (VolumetricObject
   * .ApplyCleanRootForActiveFrame / ApplyCutawayCleanForActiveFrame): Clean Root takes it as the
   * shader's second volume with the root region mask and the clean TF, plus the STJ / VBR clip
   * planes; Emptied binds the clean volume itself with the clean TF. A bundle still downloading
   * leaves the raw CT bound and is applied when it lands, if the frame and preset still match.
   */
  private async bindCleanVolume(): Promise<void> {
    const v = this.volume;
    if (!v || this.disposed) return;
    const request = ++this.cleanRequest;
    const frame = this.state.frame;
    const preset = PRESETS.find((p) => p.name === this.state.presetName);
    const mode = preset?.cleanVolume ?? 'none';
    const unbind = (status: CleanRootStatus) => {
      v.setCleanRoot(null);
      this.setRootClipPlanes(null);
      const raw = this.textures.get(frame);
      if (raw && v.material.uniforms.uData.value !== raw) {
        v.setDataTexture(raw);
        this.mpr?.invalidate();
      }
      if (this.state.cleanRootStatus !== status) this.set({ cleanRootStatus: status });
      this.viewer.invalidate();
    };
    if (mode === 'none') return unbind('off');
    if (!cleanRootAssets(this.manifest.assets, frame)) return unbind('missing');
    let entry = this.cleanFrames.get(frame);
    if (!entry) {
      unbind('loading');
      try {
        entry = await this.loadCleanFrame(frame);
      } catch (e) {
        console.warn(`clean volume unavailable for frame ${frame}`, e);
        if (request === this.cleanRequest && !this.disposed) this.set({ cleanRootStatus: 'failed' });
        return;
      }
      if (request !== this.cleanRequest || this.disposed) return;
    }
    if (mode === 'split') {
      // The raw frame stays bound (an outside view IS the Cardiac VR look); without a region
      // mask the split has nothing to switch on, so the tile keeps the Endocast look.
      if (!entry.region) return unbind('missing');
      const raw = this.textures.get(frame);
      if (raw) v.setDataTexture(raw);
      v.setCleanRoot({ texture: entry.texture, region: entry.region, tf: entry.tf });
      this.setRootClipPlanes(this.rootClipPlanesMm());
    } else {
      // Emptied: the clean volume replaces the frame's CT (the MPR slices follow), under the
      // run's clean TF when it shipped one. Projects without the per-scan TF keep the bundled
      // clean TF with the record's measured ceiling as the window.
      v.setCleanRoot(null);
      this.setRootClipPlanes(null);
      v.setDataTexture(entry.texture);
      this.mpr?.invalidate();
      if (!this.state.tfEdited) {
        const tf = tfEdit.clone(entry.tf);
        v.setTransferFunction(tf);
        this.set({ tf });
      }
      const window = entry.hasOwnTf ? ([0, 1] as [number, number]) : (entry.window ?? preset?.window ?? [0, 1]);
      if (window[0] !== this.state.window[0] || window[1] !== this.state.window[1]) this.setWindow(window[0], window[1]);
    }
    this.set({ cleanRootStatus: 'ready' });
    this.requestHistogram();
    this.viewer.invalidate();
  }

  /** Read one frame's clean bundle (volume, region mask, .tf) and upload it; deduplicated per frame. */
  private loadCleanFrame(frame: number): Promise<CleanFrame> {
    const cached = this.cleanFrames.get(frame);
    if (cached) return Promise.resolve(cached);
    const pending = this.cleanLoads.get(frame);
    if (pending) return pending;
    const assets = cleanRootAssets(this.manifest.assets, frame);
    if (!assets) return Promise.reject(new Error(`frame ${frame} has no clean volume`));
    const load = (async (): Promise<CleanFrame> => {
      const [volume, region, tfFile] = await Promise.all([
        loadVolume(assets.volume),
        assets.region
          ? loadVolume(assets.region).catch((e) => {
              console.warn('clean region mask ignored; Clean Root keeps the Endocast look', e);
              return null;
            })
          : Promise.resolve(null),
        assets.tf
          ? fetchBytes(assets.tf.url).then((b) => JSON.parse(new TextDecoder().decode(b)) as TransferFunctionJson).catch((e) => {
              console.warn('clean transfer function ignored; using the bundled clean TF', e);
              return null;
            })
          : Promise.resolve(null),
      ]);
      if (this.disposed) throw new Error('disposed');
      const grid = this.grid;
      if (grid && (volume.dims[0] !== grid.dims[0] || volume.dims[1] !== grid.dims[1] || volume.dims[2] !== grid.dims[2])) {
        throw new Error(`clean volume ${assets.volume.key} is not on the CT grid`);
      }
      // The pipeline file contributes the per-scan ALPHA layout (wall band, blood cut, calcium
      // onset); the colours are the inside-root palette, as on the desktop.
      const ownTf = tfFile && Array.isArray(tfFile.alphaPoints) && tfFile.alphaPoints.length > 0 ? applyCleanRootPalette(tfFile) : null;
      const entry: CleanFrame = {
        texture: makeData3DTexture(volume),
        region: region ? makeData3DTexture(region) : null,
        tf: ownTf ?? tfEdit.clone(TF_CARDIAC_VR_CUTAWAY_CLEAN),
        hasOwnTf: !!ownTf,
        window: cutawayCleanWindow(assets.volume.meta),
        byteLength: volume.bytes.byteLength + (region?.bytes.byteLength ?? 0),
      };
      this.cleanFrames.set(frame, entry);
      this.set({ bytesLoaded: this.state.bytesLoaded + entry.byteLength });
      return entry;
    })().finally(() => this.cleanLoads.delete(frame));
    this.cleanLoads.set(frame, load);
    return load;
  }

  /** The Clean Root tile's two clip planes from the VBR and STJ rings (scene mm), or null. */
  private rootClipPlanesMm(): ReturnType<typeof cleanRootClipPlanes> {
    const pred = this.prediction?.prediction;
    if (!pred) return null;
    return cleanRootClipPlanes(pred.rings.get('VBR') ?? [], pred.rings.get('STJ') ?? []);
  }

  /** Install, move or (null) remove the STJ / VBR clip planes (VolumetricObject.SetRootClipPlanes). */
  private setRootClipPlanes(planes: ReturnType<typeof cleanRootClipPlanes>): void {
    const v = this.volume;
    if (!v) return;
    const place = (id: string, plane: { position: THREE.Vector3; cutSide: THREE.Vector3 } | undefined) => {
      if (!plane) {
        v.removeCrossSection(id);
        return;
      }
      const existing = v.getCrossSection(id);
      const tool = existing?.object ?? new THREE.Object3D();
      tool.position.copy(plane.position);
      // The shader clips a plane's local +z half-space: point +z away from the root.
      tool.quaternion.copy(lookRotation(plane.cutSide, Math.abs(plane.cutSide.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)));
      if (!existing) {
        this.viewer.root.add(tool);
        v.addCrossSection({ id, type: 'plane', object: tool, enabled: true });
      } else v.refreshCrossSections();
    };
    place(this.rootClipStjId, planes?.stj);
    place(this.rootClipVbrId, planes?.vbr);
  }

  /** Landmarks changed (a correction) or the phase did: the planes follow the rings. */
  private refreshRootClipPlanes(): void {
    if (!this.volume?.cleanRootBound) return;
    this.setRootClipPlanes(this.rootClipPlanesMm());
    this.viewer.invalidate('main');
  }

  /** The Clean Root tile's raw-set / clean-set transparency sliders, as opacity (0..1). */
  setRenderSetOpacity(set: 'raw' | 'clean', opacity: number): void {
    const o = Math.min(1, Math.max(0, opacity));
    const raw = set === 'raw' ? o : this.state.rawSetOpacity;
    const clean = set === 'clean' ? o : this.state.cleanSetOpacity;
    this.volume?.setRenderSetOpacities(raw, clean);
    this.set({ rawSetOpacity: raw, cleanSetOpacity: clean });
    this.viewer.invalidate('main');
  }

  // ------------------------------------------------------------ leaflet overlay
  /** The frame's leaflet field assets, or null when the run ships none for it. */
  private leafletAssetsFor(frame: number): ReturnType<typeof leafletFieldAssets> {
    return leafletFieldAssets(this.manifest.assets, frame);
  }

  /**
   * Bind the active frame's leaflet field to the volume and the MPR set while the preset wants
   * the overlay (VolumetricObject.ApplyPipelineLeafletFieldForFrame): synchronously when it has
   * been read, otherwise unbind now (a wrong-phase leaflet surface is worse than a briefly
   * missing one) and bind when the read lands, if the frame and preset still match.
   */
  private async bindLeafletField(): Promise<void> {
    const v = this.volume;
    if (!v || this.disposed) return;
    const request = ++this.cuspRequest;
    const frame = this.state.frame;
    const unbind = (status: LeafletStatus) => {
      v.setCuspField(null);
      this.mpr?.setCuspField(null);
      if (this.state.leafletStatus !== status) this.set({ leafletStatus: status });
      this.viewer.invalidate();
    };
    if (!this.state.leafletOverlay) return unbind('off');
    const assets = this.leafletAssetsFor(frame);
    if (!assets) return unbind('missing');
    let entry = this.cuspFields.get(frame);
    if (!entry) {
      unbind('loading');
      try {
        entry = await this.loadCuspField(frame);
      } catch (e) {
        console.warn(`leaflet field unavailable for frame ${frame}`, e);
        if (request === this.cuspRequest && !this.disposed) this.set({ leafletStatus: 'failed' });
        return;
      }
      if (request !== this.cuspRequest || this.disposed) return;
    }
    v.setCuspField(entry.binding);
    v.setCuspPlane(this.cuspPlaneFor(frame, entry.record));
    this.mpr?.setCuspField(entry.binding.texture, entry.binding.distMaxMm, entry.binding.t0Mm, entry.binding.t1Mm);
    this.mpr?.setCuspPlane(this.cuspPlaneFor(frame, entry.record));
    this.set({ leafletStatus: 'ready' });
    this.viewer.invalidate();
  }

  /** Read one frame's field (RGBA, plus the R8 full-leaflet companion when shipped) and upload it; deduplicated per frame. */
  private loadCuspField(frame: number): Promise<CuspFrame> {
    const cached = this.cuspFields.get(frame);
    if (cached) return Promise.resolve(cached);
    const pending = this.cuspLoads.get(frame);
    if (pending) return pending;
    const assets = this.leafletAssetsFor(frame);
    if (!assets) return Promise.reject(new Error(`frame ${frame} has no leaflet field`));
    const load = (async (): Promise<CuspFrame> => {
      const field = await loadVolume(assets.field);
      if (field.channels !== 4) throw new Error(`leaflet field ${assets.field.key} is not RGBA`);
      let full: VolumeData | null = null;
      if (assets.full) {
        try {
          full = await loadVolume(assets.full);
        } catch (e) {
          console.warn('full-leaflet field ignored; the leaflet-fill slider clamps at 1', e);
        }
      }
      if (this.disposed) throw new Error('disposed');
      const record = parseLeafletFieldRecord(assets.field.meta);
      const entry: CuspFrame = {
        binding: { texture: makeData3DTexture(field), full: full ? makeData3DTexture(full) : null, dims: field.dims, distMaxMm: record.distMaxMm, t0Mm: record.t0Mm, t1Mm: record.t1Mm },
        record,
        byteLength: field.bytes.byteLength + (full?.bytes.byteLength ?? 0),
      };
      this.cuspFields.set(frame, entry);
      this.set({ bytesLoaded: this.state.bytesLoaded + entry.byteLength });
      return entry;
    })().finally(() => this.cuspLoads.delete(frame));
    this.cuspLoads.set(frame, load);
    return load;
  }

  /** The commissural cap-cutoff plane of a frame from its RN / RL / LN landmarks (corrected
   * rows included); null (cutoff off) when a commissure is missing or the grid is unknown. */
  private cuspPlaneFor(frame: number, record: LeafletFieldRecord): THREE.Vector4 | null {
    const pred = this.prediction?.prediction;
    const grid = this.grid;
    if (!pred || !grid) return null;
    const uvw = (p: THREE.Vector3) => sceneToUvw(p, grid);
    const pts = (['RN', 'RL', 'LN'] as const).map((id) => findPoint(pred, 'Commissure', id, frame)?.position);
    if (pts.some((p) => !p)) return null;
    // The compartments sit on the VBR side of the commissures; the export's centroid when it has one.
    const vbr = pred.planes.get('VBR')?.center;
    const centroid = record.centroidUvw ?? (vbr ? uvw(vbr) : new THREE.Vector3(0.5, 0.5, 0.5));
    return cuspCapPlane([uvw(pts[0]!), uvw(pts[1]!), uvw(pts[2]!)], centroid, volumeExtentMm(grid));
  }

  /** Landmarks changed (a correction): re-derive the bound frame's cap plane. */
  private refreshCuspPlane(): void {
    const v = this.volume;
    if (!v || !v.cuspFieldBound) return;
    const entry = this.cuspFields.get(this.state.frame);
    if (!entry) return;
    const plane = this.cuspPlaneFor(this.state.frame, entry.record);
    v.setCuspPlane(plane);
    this.mpr?.setCuspPlane(plane);
    this.viewer.invalidate();
  }

  /** "Leaflet fill" slider (0..2). */
  setLeafletFill(fill: number): void {
    const f = Math.min(2, Math.max(0, fill));
    this.volume?.setLeafletLook({ fill: f });
    this.mpr?.setLeafletFill(f);
    this.set({ leafletFill: f });
    this.viewer.invalidate();
  }

  /** The Clean Root tile's leaflet-set transparency slider, as opacity (0..1). */
  setLeafletOpacity(opacity: number): void {
    const o = Math.min(1, Math.max(0, opacity));
    this.volume?.setLeafletLook({ opacity: o });
    this.set({ leafletOpacity: o });
    this.viewer.invalidate('main');
  }

  /** "Show leaflet fill in orthoviews". */
  setMprLeaflets(on: boolean): void {
    this.mpr?.setLeafletOverlay(on);
    this.set({ mprLeaflets: on });
  }

  // ------------------------------------------------------------ transfer function controls
  /** The histogram editor's edits: the authored points change, the domain stays. */
  setTransferFunction(tf: TransferFunctionJson): void {
    const v = this.volume;
    if (!v) return;
    v.setTransferFunction(tf);
    this.set({ tf, tfEdited: true, tfDomain: v.domain });
    this.viewer.invalidate('main');
  }

  /** TF shift slider (HU, -1024..1024): moves the whole effective curve; the points stay. */
  setTfShiftHu(hu: number): void {
    const v = this.volume;
    if (!v) return;
    const clamped = Math.min(1024, Math.max(-1024, Math.round(hu)));
    v.setDomain(shiftHuToNormalized(clamped, this.huRange), this.state.tfStretch);
    this.set({ tfShiftHu: clamped, tfDomain: v.domain });
    this.viewer.invalidate('main');
  }

  /** The - / + buttons: TF_SHIFT_NUDGE_HU = 50. */
  nudgeTfShift(deltaHu: number): void {
    this.setTfShiftHu(this.state.tfShiftHu + deltaHu);
  }

  /** TF stretch slider (0.5..2) about the visibility-window centre. */
  setTfStretch(stretch: number): void {
    const v = this.volume;
    if (!v) return;
    const s = Math.min(2, Math.max(0.5, stretch));
    v.setDomain(shiftHuToNormalized(this.state.tfShiftHu, this.huRange), s);
    this.set({ tfStretch: s, tfDomain: v.domain });
    this.viewer.invalidate('main');
  }

  /** Reset TF: shift 0, stretch 1 and the active preset's authored curve; window and opacity stay. */
  resetTransferFunction(): void {
    const v = this.volume;
    const preset = PRESETS.find((p) => p.name === this.state.presetName);
    if (!v || !preset) return;
    v.setDomain(0, 1);
    const tf = tfEdit.clone(preset.tf);
    v.setTransferFunction(tf);
    this.set({ tf, tfShiftHu: 0, tfStretch: 1, tfEdited: false, tfDomain: v.domain });
    this.viewer.invalidate('main');
  }

  /** Min HU / Max HU fields: clamped to the volume's HU range, max >= min + 1 HU. */
  setWindowHu(minHu: number, maxHu: number): void {
    const [lo, hi] = this.huRange;
    const a = Math.min(hi - 1, Math.max(lo, minHu));
    const b = Math.min(hi, Math.max(a + 1, maxHu));
    this.setWindow(huToNormalized(a, lo, hi), huToNormalized(b, lo, hi));
  }

  windowHu(): [number, number] {
    const [lo, hi] = this.huRange;
    return [Math.round(normalizedToHu(this.state.window[0], lo, hi)), Math.round(normalizedToHu(this.state.window[1], lo, hi))];
  }

  /** Gradient threshold (isosurface mode): the minimum gradient magnitude of a hit. */
  setGradientThreshold(t: number): void {
    const v = Math.min(0.2, Math.max(0, t));
    this.volume?.setGradientVisibilityThreshold(v);
    this.set({ gradientThreshold: v });
    this.viewer.invalidate('main');
  }

  setHistogramRegionOnly(on: boolean): void {
    this.set({ histogramRegionOnly: on });
    this.requestHistogram();
  }

  /** Debounced (0.15 s like the desktop) rebuild of the editor's histogram. */
  requestHistogram(): void {
    if (this.histogramTimer) window.clearTimeout(this.histogramTimer);
    this.histogramTimer = window.setTimeout(() => {
      this.histogramTimer = null;
      this.computeHistogram();
    }, 150);
  }

  /** RenderRegionHistogramBuilder: count the voxels inside the cutout box and the isolation
   * mask when those are active, else the whole cropped dataset. */
  private computeHistogram(): void {
    const v = this.volume;
    if (!v || this.disposed) return;
    const ct = this.manifest.assets.find((a) => a.kind === 'ct' && (a.frame_index ?? 0) === this.state.frame);
    const data = ct ? this.volumeCache.get(ct.key)?.data : undefined;
    if (!data) return;
    const [W, H, D] = data.dims;
    const vs = data.voxelSize;
    let include: ((voxel: number) => boolean) | undefined;
    let caption = 'Histogram: whole volume (cropped dataset)';
    if (this.state.histogramRegionOnly) {
      caption = 'Histogram: rendered region (cutout box + isolation mask)';
      const crop = v.getCrossSection(this.rootCropId);
      let lo = [0, 0, 0];
      let hi = [W - 1, H - 1, D - 1];
      if (crop?.enabled) {
        const c = crop.object.position;
        const s = crop.object.scale;
        lo = [Math.ceil((c.x - s.x / 2) / vs[0]), Math.ceil((c.y - s.y / 2) / vs[1]), Math.ceil((c.z - s.z / 2) / vs[2])];
        hi = [Math.floor((c.x + s.x / 2) / vs[0]), Math.floor((c.y + s.y / 2) / vs[1]), Math.floor((c.z + s.z / 2) / vs[2])];
      }
      const mask = this.isolationFor(this.state.presetName);
      const md = mask ? (mask.image as { data: Uint8Array; width: number; height: number; depth: number }) : null;
      if (crop?.enabled || md) {
        include = (voxel: number) => {
          const i = voxel % W;
          const t = (voxel / W) | 0;
          const j = t % H;
          const k = (t / H) | 0;
          if (i < lo[0] || i > hi[0] || j < lo[1] || j > hi[1] || k < lo[2] || k > hi[2]) return false;
          if (md) {
            const mi = Math.min(md.width - 1, (((i + 0.5) / W) * md.width) | 0);
            const mj = Math.min(md.height - 1, (((j + 0.5) / H) * md.height) | 0);
            const mk = Math.min(md.depth - 1, (((k + 0.5) / D) * md.depth) | 0);
            if (md.data[mi + md.width * (mj + md.height * mk)] < 128) return false;
          }
          return true;
        };
      }
    }
    const counts = histogramCounts(data.bytes, data.channels, include);
    this.set({ histogram: normalizeLog(counts), histogramCaption: caption });
  }

  /** RootCropBoundsBuilder: Cusp 1/2/3 meshes + VBR and STJ ring points, +3 mm per side. */
  private setRootCrop(on: boolean): void {
    const v = this.volume;
    if (!v) return;
    if (!on) {
      v.removeCrossSection(this.rootCropId);
      return;
    }
    const box = new THREE.Box3();
    for (const role of ['cusp_1', 'cusp_2', 'cusp_3']) {
      const s = this.surfaces.byRole(role);
      if (s) box.union(s.boundsMm);
    }
    if (this.prediction) {
      for (const key of ['VBR', 'STJ']) for (const q of this.prediction.prediction.rings.get(key) ?? []) box.expandByPoint(q);
    }
    if (box.isEmpty()) return;
    box.expandByScalar(3);
    const tool = new THREE.Object3D();
    tool.position.copy(box.getCenter(new THREE.Vector3()));
    tool.scale.copy(box.getSize(new THREE.Vector3()));
    this.viewer.root.add(tool);
    v.addCrossSection({ id: this.rootCropId, type: 'boxIncl', object: tool, enabled: true });
  }

  setWindow(min: number, max: number): void {
    this.volume?.setVisibilityWindow(min, max);
    // the stretch pivots on the window centre, so the domain follows the window
    this.set({ window: [min, max], tfDomain: this.volume?.domain ?? this.state.tfDomain });
    this.viewer.invalidate('main');
  }

  setBoxOpacity(v: number): void {
    this.volume?.setBoxOpacity(v);
    this.set({ boxOpacity: v });
    this.viewer.invalidate('main');
  }

  setCameraClip(v: number): void {
    const c = Math.min(1, Math.max(0, v));
    this.volume?.setCameraClipDistance(c);
    this.set({ cameraClip: c });
    this.viewer.invalidate('main');
  }

  setLighting(on: boolean): void {
    this.volume?.setLightingEnabled(on);
    this.set({ lighting: on });
    this.viewer.invalidate('main');
  }

  setRenderMode(mode: RenderMode): void {
    this.volume?.setRenderMode(mode);
    this.set({ renderMode: mode });
    this.viewer.invalidate('main');
  }

  setQuality(samplingMultiplier: number, cubic: boolean): void {
    this.viewer.restQuality = { samplingMultiplier, cubic };
    this.viewer.applyQuality();
    this.set({ samplingMultiplier, cubic });
    this.viewer.invalidate('main');
  }

  setReduceWhileMoving(on: boolean): void {
    this.viewer.reduceWhileInteracting = on;
    this.set({ reduceWhileMoving: on });
  }

  setShowVolume(on: boolean): void {
    if (this.volume) this.volume.mesh.visible = on;
    this.set({ showVolume: on });
    this.viewer.invalidate('main');
  }

  setOrthographic(on: boolean): void {
    this.viewer.controls.setOrthographic(on);
    this.set({ orthographic: on });
  }

  private chordStart: { window: [number, number]; clip: number } | null = null;
  private windowLevelChord(dx: number, dy: number, phase: 'start' | 'move' | 'end'): void {
    if (phase === 'start') this.chordStart = { window: this.state.window, clip: this.state.cameraClip };
    if (phase !== 'move' || !this.chordStart) return;
    const [lo, hi] = this.chordStart.window;
    let center = 0.5 * (lo + hi) + dy * 0.0015;
    const width = Math.min(1, Math.max(0.001, (hi - lo) * Math.exp(dx * 0.01)));
    center = Math.min(1 - width / 2, Math.max(width / 2, center));
    this.setWindow(center - width / 2, center + width / 2);
    void width;
  }

  private cameraClipChord(dy: number, phase: 'start' | 'move' | 'end'): void {
    if (phase === 'start') this.chordStart = { window: this.state.window, clip: this.state.cameraClip };
    if (phase !== 'move' || !this.chordStart) return;
    this.setCameraClip(this.chordStart.clip + dy * 0.002);
  }

  // ------------------------------------------------------------ camera
  frameCamera(): void {
    if (!this.volume) return;
    const box = this.viewer.worldBox([this.volume.mesh]);
    this.viewer.controls.focusOnBox(box, 1.2, false);
    const coapt = this.prediction?.coaptation;
    if (coapt) {
      const world = coapt.clone().applyMatrix4(this.viewer.root.matrixWorld);
      this.viewer.controls.focusOnPoint(world, 0.3, 0.05, true);
    }
    this.viewer.invalidate('main');
  }

  focusVolume(): void {
    if (!this.volume) return;
    this.viewer.controls.focusOnBox(this.viewer.worldBox([this.volume.mesh]), 1.2, true);
  }

  /** Patient-direction snap: A/P/L/R/S/I letters map to the volume's local axes. */
  snapToLabel(letter: string): void {
    const dir = this.axisDirection(letter);
    if (!dir) return;
    const s = this.axisDirection(this.state.axisLabels.yPositive) ?? new THREE.Vector3(0, 1, 0);
    const a = this.axisDirection(this.state.axisLabels.zNegative) ?? new THREE.Vector3(0, 0, 1);
    this.viewer.controls.snapTo(dir, s, a);
    this.set({ commissureView: null });
  }

  /** World direction of the volume axis carrying a patient label. */
  axisDirection(label: string): THREE.Vector3 | null {
    const l = this.state.axisLabels;
    const local =
      label === l.xPositive ? new THREE.Vector3(1, 0, 0)
      : label === l.xNegative ? new THREE.Vector3(-1, 0, 0)
      : label === l.yPositive ? new THREE.Vector3(0, 1, 0)
      : label === l.yNegative ? new THREE.Vector3(0, -1, 0)
      : label === l.zPositive ? new THREE.Vector3(0, 0, 1)
      : label === l.zNegative ? new THREE.Vector3(0, 0, -1)
      : null;
    if (!local) return null;
    return local.transformDirection(this.viewer.root.matrixWorld).normalize();
  }

  // ------------------------------------------------------------ commissural views
  setCommissureView(id: CommissureId | null): void {
    const v = this.volume;
    const pred = this.prediction;
    if (!v) return;
    if (id === null || id === this.state.commissureView) {
      v.removeCrossSection(this.viewClipId);
      this.set({ commissureView: null });
      this.viewer.invalidate('main');
      return;
    }
    if (!pred) return;
    const commissure = pred.commissures[id];
    if (!commissure) return;
    const axisPts = this.rootAxisMm();
    if (!axisPts) return;
    const toWorld = (p: THREE.Vector3) => p.clone().applyMatrix4(this.viewer.root.matrixWorld);
    const bottom = toWorld(axisPts[0]);
    const top = toWorld(axisPts[1]);
    const c = toWorld(commissure);
    const pivot = bottom.clone().add(top).multiplyScalar(0.5);
    let radius = 0;
    const framing = [bottom, top, ...pred.framingPoints().map(toWorld)];
    for (const q of framing) radius = Math.max(radius, q.distanceTo(pivot));
    if (framing.length <= 2) radius = Math.max(radius * 2, 0.1);
    radius = Math.max(radius, 1e-3);
    const cam = this.viewer.controls.perspective;
    const vHalf = THREE.MathUtils.degToRad(cam.fov / 2);
    const hHalf = Math.atan(Math.tan(vHalf) * cam.aspect);
    const half = Math.min(vHalf, hHalf);
    const rootDistance = Math.max((radius * 1.15) / Math.sin(half), cam.near * 4 + radius);
    const axisSpan = top.clone().sub(bottom);
    const axis = axisSpan.clone().normalize();
    const foot = bottom.clone().add(axis.clone().multiplyScalar(c.clone().sub(bottom).dot(axis)));
    const radial = c.clone().sub(foot).projectOnPlane(axis);
    if (radial.lengthSq() < 1e-10) return;
    radial.normalize();
    const distance = rootDistance * 0.8;
    const rise = distance * Math.tan(THREE.MathUtils.degToRad(20));
    const position = foot.clone().sub(radial.clone().multiplyScalar(distance)).add(axis.clone().multiplyScalar(rise));
    const lookTarget = c.clone().sub(axis.clone().multiplyScalar(0.4 * axisSpan.length()));
    this.viewer.controls.setPose(position, lookTarget, axis);
    // Clip plane through the root centroid line, removing the camera's half (cut side = -radial).
    const tool = new THREE.Object3D();
    const footMm = foot.clone().applyMatrix4(new THREE.Matrix4().copy(this.viewer.root.matrixWorld).invert());
    tool.position.copy(footMm);
    const cutSideMm = radial.clone().negate().transformDirection(new THREE.Matrix4().copy(this.viewer.root.matrixWorld).invert()).normalize();
    tool.quaternion.copy(lookRotation(cutSideMm, Math.abs(cutSideMm.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)));
    this.viewer.root.add(tool);
    v.addCrossSection({ id: this.viewClipId, type: 'plane', object: tool, enabled: true });
    if (this.state.presetName !== 'Clean Root') this.applyPreset('Clean Root');
    this.set({ commissureView: id });
    this.viewer.invalidate('main');
  }

  /** Root centroid axis endpoints (scene mm), inferior -> superior. */
  private rootAxisMm(): [THREE.Vector3, THREE.Vector3] | null {
    const pred = this.prediction;
    const centerline = this.surfaces.find((i) => i.isLine && i.name.toLowerCase().startsWith('centerline'));
    if (centerline?.vertices && centerline.vertices.length >= 2 && pred) {
      const pts = this.orderedCenterline(centerline.vertices, pred.prediction);
      return [pts[0], pts[pts.length - 1]];
    }
    if (pred?.vbrPlane) {
      const n = pred.vbrPlane.normal.clone();
      const stj = pred.ringMetrics.get('STJ')?.centroid;
      if (stj && stj.clone().sub(pred.vbrPlane.center).dot(n) < 0) n.negate();
      const rise = stj ? Math.abs(stj.clone().sub(pred.vbrPlane.center).dot(n)) : 20;
      return [pred.vbrPlane.center.clone(), pred.vbrPlane.center.clone().add(n.multiplyScalar(rise))];
    }
    return null;
  }

  // ------------------------------------------------------------ MPR controls
  setMprWindow(min: number, max: number): void {
    this.mpr?.setWindow(min, max);
    this.set({ mprWindow: this.mpr?.window ?? [min, max] });
  }

  mprWindowLevelDrag(start: [number, number], dx: number, dy: number): void {
    this.mpr?.windowLevelDrag(start, dx, dy);
    this.set({ mprWindow: this.mpr?.window ?? start });
  }

  setMprGain(v: number): void {
    if (this.mpr) this.mpr.gain = v;
    this.set({ mprGain: v });
    this.mpr?.invalidate();
  }

  setMprContrast(v: number): void {
    if (this.mpr) this.mpr.contrast = v;
    this.set({ mprContrast: v });
    this.mpr?.invalidate();
  }

  setMprZoom(v: number, pane: PaneId | 'all' = 'all'): void {
    this.mpr?.setZoom(pane, v);
    if (pane === 'all') this.set({ mprZoom: v });
    else this.emit();
  }

  setMprOffset(pane: PaneId, v: number): void {
    this.mpr?.setOffset(pane, v);
    this.set({ mprOffsets: { ...this.state.mprOffsets, [pane]: v } });
  }

  syncMprOffsets(): void {
    if (!this.mpr) return;
    this.set({ mprOffsets: { x: this.mpr.panes.x.offset, y: this.mpr.panes.y.offset, z: this.mpr.panes.z.offset } });
  }

  setMprOverlays(on: boolean): void {
    this.set({ mprOverlays: on });
    this.refreshMprOverlays();
  }

  setMprLabels(on: boolean): void {
    this.set({ mprLabels: on });
    this.mpr?.setLabels(on ? this.labelTexture : null, this.labelLut);
  }

  setMprPlaneIn3D(pane: PaneId, on: boolean): void {
    this.mpr?.setPlaneVisibleIn3D(pane, on);
    this.set({ mprPlanesIn3D: { ...this.state.mprPlanesIn3D, [pane]: on } });
  }

  // ------------------------------------------------------------ frames / cine
  get frameCount(): number {
    return Math.max(1, this.manifest.num_frames);
  }

  /** Serial of the latest frame request; an older request that resolves later is dropped. */
  private frameRequest = 0;

  async setFrame(index: number): Promise<void> {
    if (!this.volume || this.disposed) return;
    if (index === this.state.frame && this.state.pendingFrame === null) return;
    const ct = this.manifest.assets.find((a) => a.kind === 'ct' && (a.frame_index ?? 0) === index);
    if (!ct) return;
    const request = ++this.frameRequest;
    let tex = this.textures.get(index);
    if (!tex) {
      // The volume has to come down from S3 first: say so, and let a newer click win.
      this.set({ pendingFrame: index, frameError: null });
      try {
        tex = await this.volumeTexture(ct);
      } catch (e) {
        if (request === this.frameRequest && !this.disposed) this.set({ pendingFrame: null, frameError: `Frame ${index + 1}: ${(e as Error).message}` });
        return;
      }
      if (request !== this.frameRequest || this.disposed) return;
    }
    this.volume.setDataTexture(tex);
    this.requestHistogram();
    void this.bindLeafletField();
    void this.bindCleanVolume();
    // per-frame surface copies
    for (const item of this.surfaces.items) {
      const fi = item.asset.frame_index;
      if (fi !== null && fi !== undefined) item.object.visible = fi === index && (this.state.rows.get(item.style?.colorKey ?? '')?.visible ?? false);
    }
    this.set({ frame: index, pendingFrame: null, frameError: null });
    this.viewer.invalidate();
    void this.prefetchFrame((index + 1) % this.frameCount);
  }

  private async prefetchFrame(index: number): Promise<void> {
    const ct = this.manifest.assets.find((a) => a.kind === 'ct' && (a.frame_index ?? 0) === index);
    if (!ct) return;
    try {
      if (!this.textures.has(index)) await this.volumeTexture(ct);
      // cine warm-up of the next phase's leaflet field / clean bundle while a preset uses them
      if (this.state.leafletOverlay && !this.cuspFields.has(index) && this.leafletAssetsFor(index)) await this.loadCuspField(index);
      if (this.state.cleanRootStatus !== 'off' && !this.cleanFrames.has(index) && cleanRootAssets(this.manifest.assets, index)) await this.loadCleanFrame(index);
    } catch {
      /* best effort; the click on that frame reports the failure */
    }
  }

  nextFrame(): void {
    void this.setFrame((this.state.frame + 1) % this.frameCount);
  }

  prevFrame(): void {
    void this.setFrame((this.state.frame - 1 + this.frameCount) % this.frameCount);
  }

  setFps(fps: number): void {
    this.set({ fps: Math.min(15, Math.max(0.5, fps)) });
    if (this.state.playing) this.play();
  }

  play(): void {
    if (this.frameCount < 2) return;
    this.stopCine();
    // A tick while a frame is still downloading is skipped rather than queued, so playback
    // stalls on the missing frame instead of racing ahead of it.
    this.cineTimer = window.setInterval(() => {
      if (this.state.pendingFrame === null) this.nextFrame();
    }, 1000 / this.state.fps);
    this.set({ playing: true });
  }

  pause(): void {
    this.stopCine();
    this.set({ playing: false });
  }

  private stopCine(): void {
    if (this.cineTimer) {
      window.clearInterval(this.cineTimer);
      this.cineTimer = null;
    }
  }

  // ------------------------------------------------------------ thumbnail
  private async maybeCaptureThumbnail(): Promise<void> {
    if (this.thumbnailDone || this.manifest.thumbnail_url || !this.volume || this.state.loading) return;
    this.thumbnailDone = true;
    try {
      await new Promise((r) => setTimeout(r, 1500));
      const blob = await this.viewer.capture(320, 240, 0.85);
      if (blob.size > 200 * 1024) return;
      await api.putRaw(`/api/runs/${this.manifest.run_id}/thumbnail`, blob, 'image/jpeg');
    } catch (e) {
      console.warn('thumbnail capture failed', e);
    }
  }

  // ------------------------------------------------------------ sizes
  totalAssetBytes(): number {
    return this.manifest.assets.reduce((n, a) => n + (a.bytes ?? 0), 0);
  }

  dispose(): void {
    this.disposed = true;
    this.stopCine();
    if (this.histogramTimer) window.clearTimeout(this.histogramTimer);
    if (this.rebuildTimer) window.clearTimeout(this.rebuildTimer);
    this.editController?.dispose();
    this.handleSet.dispose();
    this.prediction?.dispose();
    this.surfaces.dispose();
    this.mpr?.dispose();
    this.volume?.dispose();
    for (const t of this.textures.values()) t.dispose();
    for (const t of this.isolationTextures.values()) t.dispose();
    for (const f of this.cuspFields.values()) {
      f.binding.texture.dispose();
      f.binding.full?.dispose();
    }
    for (const c of this.cleanFrames.values()) {
      c.texture.dispose();
      c.region?.dispose();
    }
    this.labelTexture?.dispose();
  }
}

/** localStorage 'valvia.quality' = low | medium caps the rest quality (weak GPUs, software GL). */
function qualityOverride(samplingMultiplier: number, cubic: boolean): { samplingMultiplier: number; cubic: boolean } {
  let level: string | null = null;
  try {
    level = localStorage.getItem('valvia.quality');
  } catch {
    level = null;
  }
  if (level === 'low') return { samplingMultiplier: Math.min(samplingMultiplier, 0.5), cubic: false };
  if (level === 'medium') return { samplingMultiplier: Math.min(samplingMultiplier, 1), cubic: false };
  return { samplingMultiplier, cubic };
}

function withAlpha(c: Rgba, a: number): Rgba {
  return [c[0], c[1], c[2], a];
}

export { MM_PER_UNIT };
export type { AssetEntry };
export { fetchBytes };
